import { IconButton, makeStyles, Typography } from '@material-ui/core';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import NoteIcon from '@material-ui/icons/Note';
import PeopleIcon from '@material-ui/icons/People';
import WarningIcon from '@material-ui/icons/Warning';
import { useCallback, useContext, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Role } from '../../api/user/Role';
import { Venture } from '../../api/ventures/types/Venture';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { encodeQuery, getColorStatus } from '../../utils/functions';
import { Link, StatusBadge, Text } from '../common';
import VenturesRowActions from '../ventures/ventures-row-actions';

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',

    '& > span': {
      fontSize: 18,
      display: '-webkit-box',
      color: COLORS.COLOR_TEXT_BASE,
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
    },

    '& > span, & > svg': {
      verticalAlign: 'middle',
    },
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    lineHeight: '1.5',
    marginTop: 16,
  },
  iconsBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 16,

    '& > a': {
      padding: 4,
    },
  },
  actions: {
    marginLeft: 'auto',
  },
  warningIcon: {
    color: COLORS.COLOR_ADDITIONAL_ORANGE,
    fontSize: 22,
  },
}));

function VentureCard({
  venture,
  cardId,
}: {
  venture: Venture;
  cardId: string;
}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { hasRole, hasAccessToAction } = useContext(UserContext);

  const prevPath = `${location.pathname}${location.search}`;

  const search = useMemo(() => {
    return `filter=${encodeQuery({
      venture: { id: venture.id, ventureName: venture.ventureName },
    })}`;
  }, [venture.id, venture.ventureName]);

  const handleCardClick = useCallback(() => {
    history.push({
      pathname: getRoutePath(Pages.VENTURE_DETAILS, {
        ventureId: venture.id,
      }),
      state: { prevPath },
    });
  }, [history, prevPath, venture.id]);

  return (
    <div className={classes.card} id={cardId}>
      <div className={classes.cardHeader} onClick={handleCardClick}>
        <Typography variant='h5' className={classes.cardTitle}>
          {!!venture?.tags?.length && (
            <WarningIcon className={classes.warningIcon} />
          )}
          <span>{venture.ventureName}</span>
        </Typography>
        <div>
          <StatusBadge
            status={venture.status}
            variant={getColorStatus(venture.status)}
          />
        </div>
      </div>
      {!!venture.description && (
        <Text
          variant='normal'
          className={classes.description}
          onClick={handleCardClick}>
          {venture.description}
        </Text>
      )}
      <div className={classes.iconsBlock}>
        {!hasRole(Role.Founder) && (
          <IconButton
            data-testid='venture-card-sessions-icon'
            component={Link}
            to={{
              pathname: Pages.SESSIONS,
              search,
              state: {
                prevPath,
              },
            }}>
            <EventAvailableIcon
              fontSize='large'
              style={{ color: COLORS.COLOR_BLUE_BASE }}
            />
          </IconButton>
        )}
        {hasAccessToAction('venture.notes.view') && (
          <IconButton
            data-testid='venture-card-notes-icon'
            component={Link}
            to={{
              pathname: getRoutePath(Pages.VENTURE_NOTES, {
                ventureId: venture.id,
              }),
              state: {
                prevPath,
              },
            }}>
            <NoteIcon
              fontSize='large'
              style={{ color: COLORS.COLOR_BLUE_BASE }}
            />
          </IconButton>
        )}
        {hasAccessToAction('venture.assignemnts.view') && (
          <IconButton
            data-testid='venture-card-mentors-icon'
            component={Link}
            to={{
              pathname: getRoutePath(Pages.VENTURE_ASSIGNMENTS, {
                ventureId: venture.id,
              }),
              state: {
                prevPath,
              },
            }}>
            <PeopleIcon
              fontSize='large'
              style={{ color: COLORS.COLOR_BLUE_BASE }}
            />
          </IconButton>
        )}
        <div className={classes.actions}>
          <VenturesRowActions venture={venture} variant='list' />
        </div>
      </div>
    </div>
  );
}

export default VentureCard;
